import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import Vue from 'vue';
import PostExcelForOrganisationRequestModel from '../../../../../services/v2/model/post-excel-for-organisation-request-model';
import LookupRequestModel from '../../../../../services/v2/model/lookup-request-model';
import GetFeedbackByOrganisationIdRequestModel from '../../../../../services/v2/model/get-feedback-by-organisation-id-request-model';
import GuidEventLookupModel from '../../../../../services/v2/model/guid-event-lookup-model';

export default class FeedbacksPage extends PageBase {
  public get organisationFeedbacksList() {
    if (this.organisationFeedbacks.data !== undefined && this.organisationFeedbacks.data.items !== undefined) {
      return this.organisationFeedbacks.data?.items?.map((record) => ({
        id: record.data.id,
        companyName: record.data.companyName,
        eventRating: Vue.$tEnum('page.dashboard.companies.feedbacks.enum.eventRating', record.data.eventRating!),
        reparticipationRating: Vue.$tEnum('page.dashboard.companies.feedbacks.enum.reparticipationRating', record.data.reparticipationRating!),
        youthVisitorsCount: record.data.youthVisitorsCount,
        adultVisitorsCount: record.data.adultVisitorsCount,
        remark: (record.data.remark?.length ?? 0) > 10 ? `${record.data.remark?.substring(0, 10)}...` : (record.data.remark || ''),
      }));
    }
    return [];
  }

  public set organisationFeedbacksList(value) { /* Do nothing */ }

  public isLoadingExcel = false;

  public eventItems: GuidEventLookupModel[] | undefined;

  private organisationsDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Organisations.getOrganisationForUser,
  });

  public organisationFeedbacks = new DataSource({
    selectCommand: () => {
      const request = new GetFeedbackByOrganisationIdRequestModel();
      request.eventIdentifier = this.eventsDataSource.filter.eventIdentifier;
      request.organisationId = this.organisationId;
      request.searchCriteria = this.organisationFeedbacks.filter.searchCriteria;
      return Vue.$service.v2.api.tdg_Feedbacks.getFeedbackByOrganisationId(request);
    },
    insertCommand: () => {
      const request = new PostExcelForOrganisationRequestModel();
      request.eventIdentifier = this.eventsDataSource.filter.eventIdentifier;
      request.organisationId = this.organisationId;
      request.searchCriteria = this.organisationFeedbacks.filter.searchCriteria;
      return Vue.$service.v2.api.tdg_Feedbacks.postExcelForOrganisation(request);
    },
  });

  public eventsDataSource = new DataSource({
    selectCommand: () => {
      const req = new LookupRequestModel();
      return Vue.$service.v2.api.tdg_Events.eventLookup(req);
    },
  });

  async initialize(): Promise<void> {
    await this.eventsDataSource.select();
    await this.loadEventLookup();
    await this.organisationsDataSource.select();
    await this.refreshFeedbacks();
  }

  public get organisationId() { return this.organisationsDataSource.data?.data.id; }

  public feedbackColumns = [
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.id'), field: 'id' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.companyName'), field: 'companyName' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.eventRating'), field: 'eventRating' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.reparticipationRating'), field: 'reparticipationRating' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.youthVisitorsCount'), field: 'youthVisitorsCount' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.adultVisitorsCount'), field: 'adultVisitorsCount' },
    { value: Vue.$t('page.dashboard.organisations.feedbacks.table.remark'), field: 'remark' },
  ];

  async refreshFeedbacks() {
    try {
      this.organisationFeedbacks.filter.organisationId = this.organisationId;
      await this.organisationFeedbacks.select();
    } catch (error) {
      Vue.$log.error(`Error refreshing companies: ${error}`);
    }
  }

  public async downloadExcel() {
    this.isLoadingExcel = true;

    if (this.organisationFeedbacks.data) {
      if (this.organisationFeedbacks.data.items.length > 0) {
        const requestModel = new PostExcelForOrganisationRequestModel({
          searchCriteria: this.organisationFeedbacks.filter.searchCriteria,
          organisationId: this.organisationId,
          eventIdentifier: this.eventsDataSource.filter.eventIdentifier,
        });

        const response = await this.organisationFeedbacks.insert(requestModel);

        if (response && response.data) {
          const blob = new Blob([response.data], { type: 'application/excel' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = 'Tour-de-Gwärb-Feedbacks.xlsx';
          link.click();
        }
      }
    }
    this.isLoadingExcel = false;
  }

  async loadEventLookup() {
    if (this.eventsDataSource.data) {
      const res = this.eventsDataSource.data;

      this.eventItems = res.items.map((i) => {
        const model = new GuidEventLookupModel();
        model.id = i.data.id;
        model.caption = i.data.caption;
        return model;
      });

      if (this.eventItems.length > 0) {
        const lastItem = this.eventItems[this.eventItems.length - 1];
        this.eventsDataSource.filter.eventIdentifier = lastItem.id;
      }
    }
  }
}
